import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import logoUrl from '@assets/vector/logo/lk-logo-uncolored.svg';

interface NavbarProps {
	isLoaded?: boolean;
	activeSection?: number;
	onNavigate?: (section: number) => void;
}

export function Navbar({
	isLoaded = false,
	activeSection = 0,
	onNavigate,
}: NavbarProps) {
	const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
	const [showLogo, setShowLogo] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(false);

	const heyRef = useRef<HTMLButtonElement>(null);
	const meRef = useRef<HTMLButtonElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const hasTransitionedRef = useRef(false);
	const lastIndicatorRef = useRef({ width: 0, left: 0 });

	const activeLink = activeSection === 0 ? 'hey' : 'me';
	const isOnWhiteBackground = activeSection === 1;

	useEffect(() => {
		if (!isLoaded || hasTransitionedRef.current) {
			return;
		}

		const transitionTimer = setTimeout(() => {
			setIsTransitioning(true);
		}, 2000);

		const logoTimer = setTimeout(() => {
			setShowLogo(true);
			hasTransitionedRef.current = true;
		}, 2500);

		return () => {
			clearTimeout(transitionTimer);
			clearTimeout(logoTimer);
		};
	}, [isLoaded]);

	const updateIndicator = useCallback(() => {
		const container = containerRef.current;
		const target = activeLink === 'hey' ? heyRef.current : meRef.current;

		if (!container || !target) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		const newWidth = targetRect.width;
		const newLeft = targetRect.left - containerRect.left;

		const last = lastIndicatorRef.current;

		if (last.width === newWidth && last.left === newLeft) {
			return;
		}

		lastIndicatorRef.current = { width: newWidth, left: newLeft };
		setIndicatorStyle({ width: newWidth, left: newLeft });
	}, [activeLink]);

	useEffect(() => {
		const rafId = requestAnimationFrame(updateIndicator);
		window.addEventListener('resize', updateIndicator);

		return () => {
			cancelAnimationFrame(rafId);
			window.removeEventListener('resize', updateIndicator);
		};
	}, [updateIndicator]);

	return (
		<motion.nav
			initial={{ opacity: 0, y: -20 }}
			animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
			transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.1 }}
			className="fixed top-0 left-1/2 -translate-x-1/2 z-50 mt-2 sm:mt-4 md:mt-6 pointer-events-auto"
			aria-label="Main navigation"
		>
			<div
				className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full backdrop-blur-md shadow-lg transition-all duration-300 ease-out ${
					isOnWhiteBackground
						? 'bg-background/90 border border-primary/80'
						: 'bg-background/10 border border-white/80'
				}`}
			>
				<div className="relative w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0">
					<span
						className={`absolute inset-0 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-lg sm:text-xl transition-opacity duration-500 ease-in-out ${
							isTransitioning ? 'opacity-0' : 'opacity-100'
						}`}
						style={
							!showLogo
								? {
										animation: 'wave 0.5s ease-in-out infinite',
									}
								: undefined
						}
						aria-hidden="true"
					>
						👋
					</span>
					<img
						src={logoUrl}
						alt=""
						width={24}
						height={24}
						className={`absolute inset-0 w-5 h-5 sm:w-6 sm:h-6 transition-opacity duration-500 ease-in-out ${
							showLogo ? 'opacity-100' : 'opacity-0'
						}`}
						style={{
							filter: isOnWhiteBackground
								? 'brightness(0) saturate(100%) invert(8%) sepia(100%) saturate(7492%) hue-rotate(240deg) brightness(99%) contrast(101%)'
								: 'brightness(0) invert(1)',
						}}
						loading="eager"
						decoding="async"
						fetchPriority="high"
						aria-hidden="true"
					/>
				</div>

				<div ref={containerRef} className="relative flex items-center gap-1">
					<div
						className={`absolute top-1/2 -translate-y-1/2 rounded-full transition-colors duration-300 ease-in-out ${
							isOnWhiteBackground ? 'bg-primary' : 'bg-white'
						}`}
						style={{
							width: `${indicatorStyle.width}px`,
							left: `${indicatorStyle.left}px`,
							height: 'calc(100% - 4px)',
							transition:
								'width 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94), left 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 300ms ease-out',
						}}
					/>
					<button
						ref={heyRef}
						onClick={onNavigate ? () => onNavigate(0) : undefined}
						className={`relative px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-sm sm:text-base font-medium transition-colors duration-200 ease-out z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							isOnWhiteBackground
								? activeLink === 'hey'
									? 'text-white focus-visible:ring-white'
									: 'text-primary focus-visible:ring-primary'
								: activeLink === 'hey'
									? 'text-primary focus-visible:ring-primary'
									: 'text-white focus-visible:ring-white'
						}`}
						aria-current={activeLink === 'hey' ? 'page' : undefined}
					>
						hey
					</button>
					<button
						ref={meRef}
						onClick={onNavigate ? () => onNavigate(1) : undefined}
						className={`relative px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-sm sm:text-base font-medium transition-colors duration-200 ease-out z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							isOnWhiteBackground
								? activeLink === 'me'
									? 'text-white focus-visible:ring-white'
									: 'text-primary focus-visible:ring-primary'
								: activeLink === 'me'
									? 'text-primary focus-visible:ring-primary'
									: 'text-white focus-visible:ring-white'
						}`}
						aria-current={activeLink === 'me' ? 'page' : undefined}
					>
						me
					</button>
				</div>
			</div>
		</motion.nav>
	);
}
